import { createRouter, createWebHistory } from 'vue-router'
import { useUserStoreWithOut } from '@/store/modules/user'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/workbench',
    },
    // ---- SSO 回调页面 ----
    {
      path: '/sso-callback',
      name: 'sso-callback',
      component: () => import('@/modules/auth/pages/SsoCallbackPage.vue'),
    },
    // ---- 无需布局的认证页面 ----
    {
      path: '/login',
      name: 'login',
      beforeEnter: () => {
        if (!import.meta.env.DEV) {
          const returnUrl = encodeURIComponent(window.location.origin + '/sso-callback')
          window.location.href = `${import.meta.env.VITE_SSO_AUTHORITY}/login?redirect=${returnUrl}`
        }
      },
      component: () => import('@/modules/auth/pages/LoginPage.vue'),
    },
    {
      path: '/register',
      name: 'register',
      beforeEnter: () => {
        if (!import.meta.env.DEV) {
          const returnUrl = encodeURIComponent(window.location.origin + '/sso-callback')
          window.location.href = `${import.meta.env.VITE_SSO_AUTHORITY}/register?redirect=${returnUrl}`
        }
      },
      component: () => import('@/modules/auth/pages/RegisterPage.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      beforeEnter: () => {
        if (!import.meta.env.DEV) {
          const returnUrl = encodeURIComponent(window.location.origin + '/login')
          window.location.href = `${import.meta.env.VITE_SSO_AUTHORITY}/forgot-password?redirect=${returnUrl}`
        }
      },
      component: () => import('@/modules/auth/pages/ForgotPasswordPage.vue'),
    },
    // ---- 不需要侧边栏的页面 ----
    {
      path: '/storyboard',
      name: 'storyboard',
      meta: { requiresAuth: true },
      component: () => import('@/modules/storyboard/pages/StoryboardV2Page.vue'),
    },
    {
      path: '/storyboard-v1',
      name: 'storyboard-v1',
      meta: { requiresAuth: true },
      component: () => import('@/modules/storyboard/pages/StoryboardPage.vue'),
    },
    // ---- AppLayout 包裹的页面 ----
    {
      path: '/',
      component: () => import('@/shared/layout/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: 'workbench',
          name: 'workbench',
          component: () => import('@/modules/workbench/pages/WorkbenchPage.vue'),
        },
        {
          path: 'project/:id',
          name: 'project-detail',
          component: () => import('@/modules/project/pages/ProjectDetailPage.vue'),
        },
        {
          path: 'asset',
          name: 'assets',
          component: () => import('@/modules/assets/pages/AssetsPage.vue'),
        },
        {
          path: 'image-generate',
          name: 'image-generate',
          component: () => import('@/modules/material/pages/MaterialGeneratePage.vue'),
        },
        {
          path: 'video-generate',
          name: 'video-generate',
          component: () => import('@/modules/material/pages/VideoGeneratePage.vue'),
        },
        {
          path: 'asset/:type',
          name: 'asset-category',
          component: () => import('@/modules/assets/pages/AssetCategoryPage.vue'),
        },
        {
          path: 'asset/',
          redirect: { name: 'assets' },
        },
        {
          path: 'team',
          name: 'team',
          component: () => import('@/modules/team/pages/TeamManagementPage.vue'),
        },
        {
          path: 'invoice',
          name: 'invoice',
          component: () => import('@/modules/finance/pages/InvoicePage.vue'),
        },
        {
          path: 'purchase',
          name: 'purchase',
          component: () => import('@/modules/finance/pages/PurchaseCoinsPage.vue'),
        },
        {
          path: 'transactions',
          name: 'transactions',
          component: () => import('@/modules/finance/pages/TransactionHistoryPage.vue'),
        },
        {
          path: 'recharges',
          name: 'recharges',
          component: () => import('@/modules/finance/pages/RechargeHistoryPage.vue'),
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/modules/user/pages/ProfilePage.vue'),
        },
        {
          path: 'change-password',
          beforeEnter: () => {
            if (!import.meta.env.DEV) {
              const returnUrl = encodeURIComponent(window.location.origin + '/workbench')
              window.location.href = `${import.meta.env.VITE_SSO_AUTHORITY}/change-password?redirect=${returnUrl}`
            }
          },
          component: () => import('@/modules/auth/pages/ChangePasswordPage.vue'),
        },
        {
          path: 'messages',
          name: 'messages',
          component: () => import('@/modules/user/pages/MessagesPage.vue'),
        },
      ],
    },
  ],
})

// 无需登录即可访问的白名单
const whiteList = [...NO_REDIRECT_WHITE_LIST, '/sso-callback', '/storyboard-v2']

router.beforeEach((to) => {
  const userStore = useUserStoreWithOut()
  const token = userStore.getToken

  // 检查 URL 是否携带 sso_token（从认证中心跳回）
  const ssoToken = to.query.sso_token as string
  if (ssoToken && to.path !== '/sso-callback') {
    // 有 sso_token 但不在回调页，重定向到回调页处理
    return `/sso-callback?sso_token=${ssoToken}&redirect=${encodeURIComponent(to.fullPath)}`
  }

  if (token) {
    // 已登录
    if (to.path === '/login') {
      return '/workbench'
    }
    return
  }

  // 未登录
  if (whiteList.includes(to.path)) {
    return
  }

  // 生产/测试环境：SSO 单点登录
  if (!import.meta.env.DEV) {
    const redirectPath = encodeURIComponent(to.fullPath)
    return `/sso-callback?redirect=${redirectPath}`
  }
  // 开发环境：本地登录
  return `/login?redirect=${encodeURIComponent(to.fullPath)}`
})

export default router
